import { defineStore } from 'pinia';
import { getJobPositionsInAlphaOrder } from '@webapp-next/modules/people/composables/people-edit-work-detail';
import listJobTitles, {
  JobTitle,
} from '@/lib/request/svc/hr/list-job-titles';
import useEmploymentStore from '@/modules/people/store/employment';
import {
  Department,
  Role,
  Training,
  Workplace,
} from '@/modules/people/types/people-setup';
import Employee from '@/modules/people/types/employee';
import { ISOString } from '@/types/date-types';
import { fromISOToJSDate } from '@/modules/people/utils/date-converter';

export type WorkDetailsStoreStateLocationsBoolean = Record<
  Workplace['Id'],
  boolean
>;

export type WorkDetailsStoreStateLocation = {
  id: string; // `company_${item.Id}`,
  label: Workplace['CompanyName'];
  value: Workplace['Code'];
  noRemovePermission: boolean;
  canSelect: boolean;
  active: boolean;
  parentId: Workplace['ParentCompany'];
  link: Workplace['Id'];
};

export type WorkDetailsEndEmployment = {
  employmentEndDate?: Date | null;
  employmentEndReason?: string | null;
  employmentEndSentiment?: string | null;
  employmentEndComment?: string | null;
};

// Required fields for wa-multi-select component
export interface PayrollAccessOption {
  id: string;
  label: string;
  active: boolean;
}

type WorkDetailsStoreState = {
  locations: WorkDetailsStoreStateLocation[];
  role?: Role['Id'];
  trainings?: Training[]; // should we initialize it with []?
  trainingRecords?: Training[];
  hiredOn?: Date | null;
  position: string | null;
  validationError: Record<string, boolean | undefined>;
  manages: Department['Id'][];
  prefers: Department['Id'][];
  managesLoaded: boolean;
  prefersLoaded: boolean;
  allJobPositions: Employee['Position'][];
  /**
   * undefined until the employee read resolves it, and omitted from the save
   * payload while it stays that way. '' is a resolved "no title" and is what
   * the save DTO reads as an explicit unassign.
   */
  jobTitleId: string | undefined;
  jobTitles: JobTitle[];
  jobTitlesLoaded: boolean;
  jobTitlesLoading: boolean;
  jobTitlesError: boolean;
  businessAccessList: PayrollAccessOption[];
} & WorkDetailsEndEmployment;

const useWorkDetailsStore = defineStore('webapp:people:work-details', {
  state: (): WorkDetailsStoreState => {
    return {
      locations: [],
      role: undefined,
      trainings: undefined, // should we initialize it with []?
      trainingRecords: undefined,
      hiredOn: undefined,
      employmentEndDate: null,
      employmentEndReason: '',
      employmentEndSentiment: null,
      employmentEndComment: '',
      position: null,
      validationError: {},
      manages: [],
      prefers: [],
      managesLoaded: false,
      prefersLoaded: false,
      allJobPositions: [],
      jobTitleId: undefined,
      jobTitles: [],
      jobTitlesLoaded: false,
      jobTitlesLoading: false,
      jobTitlesError: false,
      businessAccessList: [],
    };
  },
  actions: {
    // TODO: why we have these 2 states this.trainings and this.trainingRecords, we need to simplify it
    setTraining(trainings: Training[] = []): void {
      this.trainings = trainings;
    },
    getTraining(): Training[] {
      return this.trainings || [];
    },
    // TODO: why we have these 2 states this.trainings and this.trainingRecords, we need to simplify it
    setTrainingRecords(trainingRecords = []): void {
      this.trainingRecords = trainingRecords;
    },
    setTrainingRecordByIndex(index = 0, trainingRecord: Training): void {
      if (this.trainingRecords && this.trainingRecords[index]) {
        this.trainingRecords[index] = trainingRecord || {};
      }
    },
    getTrainingRecordByIndex(index = 0): Training | undefined {
      return this.trainingRecords && this.trainingRecords[index]
        ? this.trainingRecords[index]
        : undefined;
    },
    setRole(role?: Role['Id']): void {
      this.role = role;
    },
    setHiredOn(hiredOn: Date | ISOString): void {
      this.hiredOn = fromISOToJSDate(hiredOn);
    },
    setEmploymentEndDate(date: Employee['EmploymentEndDate']): void {
      this.employmentEndDate = date ? fromISOToJSDate(date) : null;
    },
    setEmploymentEndReason(reason: Employee['EmploymentEndReason']): void {
      this.employmentEndReason = reason;
    },
    setEmploymentEndSentiment(
      sentiment: Employee['EmploymentEndSentiment']
    ): void {
      this.employmentEndSentiment = sentiment;
    },
    setEmploymentEndComment(comment: Employee['EmploymentEndComment']): void {
      this.employmentEndComment = comment;
    },
    setPosition(position: Employee['Position'] = null): void {
      this.position = position;
    },
    /** Clearing the field unassigns, which the save contract spells '' rather than null. */
    setJobTitleId(jobTitleId: string | null = null): void {
      this.jobTitleId = jobTitleId ?? '';
    },
    /**
     * Only adopts a value the read actually resolved. An absent key means
     * svc-hr was unreachable, and undefined keeps the field out of the next save
     * payload rather than submitting it as an unassign.
     *
     * The store outlives the profile dialog, so the absent case has to write
     * undefined rather than leave the property alone: skipping the write keeps
     * the previously opened employee's title, and saving then assigns their
     * title to this one.
     */
    initJobTitleId(employee: Partial<Employee>): void {
      this.jobTitleId =
        'JobTitleId' in employee ? (employee.JobTitleId ?? '') : undefined;
    },
    setJobTitles(jobTitles: JobTitle[] = []): void {
      this.jobTitles = jobTitles;
    },
    /** The selector renders a retry, so a failure is surfaced rather than swallowed. */
    async fetchJobTitles(): Promise<void> {
      this.jobTitlesLoading = true;
      this.jobTitlesError = false;

      try {
        this.setJobTitles(await listJobTitles());
      } catch (e) {
        console.error('listJobTitles failed', e);
        this.setJobTitles([]);
        this.jobTitlesError = true;
      } finally {
        this.jobTitlesLoading = false;
        this.jobTitlesLoaded = true;
      }
    },
    async fetchJobPositions(): Promise<void> {
      try {
        this.setAllJobPositions(await getJobPositionsInAlphaOrder());
      } catch (e) {
        console.error('getJobPositionsInAlphaOrder failed', e);
      }
    },
    setAllJobPositions(allJobPositions: Employee['Position'][] = []): void {
      this.allJobPositions = allJobPositions;
    },
    setLocations(locations: WorkDetailsStoreStateLocation[] = []): void {
      this.locations = locations;

      const employmentState = useEmploymentStore();
      employmentState.fixABPAreas();
    },
    removeLocationById(locationId: WorkDetailsStoreStateLocation['id']): void {
      this.setLocations(this.locations.filter(({ id }) => id !== locationId));
    },
    addLocation(location: WorkDetailsStoreStateLocation): void {
      if (location) {
        this.setLocations([...this.locations, location]);
      }
    },
    setBusinessAccessList(
      businessAccessList: PayrollAccessOption[] = []
    ): void {
      this.businessAccessList = businessAccessList;
    },
    setManages(manages: Department['Id'][] = []): void {
      this.manages = manages;
    },
    setPrefers(prefers: Department['Id'][] = []): void {
      this.prefers = prefers;
    },
    setManagesLoaded(managesLoaded: boolean): void {
      this.managesLoaded = managesLoaded;
    },
    setPrefersLoaded(prefersLoaded: boolean): void {
      this.prefersLoaded = prefersLoaded;
    },
    pruneManagedAreas(areaIds: Department['Id'][] = []): void {
      this.setManages(this.manages.filter(area => !areaIds.includes(area)));
    },
    prunePreferredAreas(areaIds: Department['Id'][] = []): void {
      this.setPrefers(this.prefers.filter(area => !areaIds.includes(area)));
    },
    getLocationByIndex(index = 0): WorkDetailsStoreStateLocation {
      return this.locations[index] || {};
    },
    getLocationIds(): Workplace['Id'][] {
      return this.locations.map(location => location.link);
    },
    getLocationIdsAsObject(): WorkDetailsStoreStateLocationsBoolean {
      return this.locations.reduce<WorkDetailsStoreStateLocationsBoolean>(
        (reduced, location) => {
          reduced[location.link] = true;
          return reduced;
        },
        {}
      );
    },
    getLocations(): WorkDetailsStoreStateLocation[] {
      return this.locations || [];
    },
    setValidationError(id: string, value?: boolean): void {
      this.validationError[id] = value;
    },
    getValidationError(id: string): boolean | undefined {
      return this.validationError[id];
    },
    setValidationErrors(errors = {}): void {
      this.validationError = errors;
    },
    getValidationErrors(): WorkDetailsStoreState['validationError'] {
      return this.validationError;
    },
  },
});

export default useWorkDetailsStore;
